/* @flow */

import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {BulletedList} from '../../../bulleted-list';
import {Button, CloseButton, CheckmarkButton} from '../../../button';
import {BaseRow} from '../../../row/base-row/base-row';
import {TextField} from '../../../textfield';

import {PotentialPeepsDropdown} from '../../potential-peeps-dropdown/potential-peeps-dropdown';

import type {AccountInput} from '../types';

import {FIELD_NAME, FIELD_ADDRESS, FIELD_DESCRIPTION} from './consts';
import {shouldShowPotentialAccounts, shouldAllowCreate, handleKeyboardNavigation} from './helpers';
import type {AccountFormFieldTypes} from './types';

import '../../peep-cards.module.css';

type Props = {|
    temporaryAccount?: {|
        id: string,
        ...AccountInput,
    |},
    onSelectAccount: (DisplayOptionEntity) => void,
    onSubmit: (AccountInput) => void,
    onSearchPotentialAccounts?: (AccountInput) => Promise<*>,
    onClose: () => void,
    isPotentialAccountsPopover?: boolean,
|};

export function AccountCardCreateable(props: Props) {
    const [name, setName] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.name ? props.temporaryAccount.name : ''
    );
    const [description, setDescription] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.description
            ? props.temporaryAccount.description
            : ''
    );
    const [address, setAddress] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.address
            ? props.temporaryAccount.address
            : ''
    );
    const [potentialAccounts, setPotentialAccounts] = React.useState<DisplayOptionEntity[]>([]);
    const [formErrors, setFormErrors] = React.useState<string[]>([]);

    const nameRef = React.useRef<?HTMLInputElement | null>(null);
    const addressRef = React.useRef<?HTMLInputElement | null>(null);
    const descriptionRef = React.useRef<?HTMLInputElement | null>(null);
    const popoverRef = React.useRef<?HTMLElement | null>(null);

    const shouldShowPotentialAccountsComponent = shouldShowPotentialAccounts(
        name,
        description,
        address
    );
    const shouldAllowCreateAccount = shouldAllowCreate(
        Boolean(props.temporaryAccount),
        name,
        address
    );

    React.useEffect(() => {
        if (shouldShowPotentialAccountsComponent) {
            props.onSearchPotentialAccounts &&
                props
                    .onSearchPotentialAccounts({
                        name,
                        description,
                        address,
                    })
                    .then((matchedAccounts: DisplayOptionEntity[]) => {
                        setPotentialAccounts(matchedAccounts);
                    });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, description, address]);

    const handleSubmitAccount = () => {
        if (!name.trim() && !address.trim()) {
            setFormErrors(['At least a company name or location is required']);

            return;
        }
        setFormErrors([]);
        props.onSubmit({
            name,
            description,
            address,
        });
    };

    const handleEnter = (e: SyntheticKeyboardEvent<>) => {
        e.preventDefault();
        handleSubmitAccount();
    };

    const getFieldHasError = () => {
        return formErrors.length > 0;
    };

    const handleKeyDown = (e: SyntheticKeyboardEvent<>, currentField: AccountFormFieldTypes) => {
        handleKeyboardNavigation(e, currentField, addressRef, descriptionRef, nameRef);
    };

    return (
        <>
            <BaseRow getRowRef={popoverRef} type='accounts'>
                <div className='flex flex-dir-col gap-8'>
                    <div className='flex gap-8'>
                        <div className='flex flex-dir-col gap-8 full-width'>
                            <TextField
                                placeholder='Company name'
                                value={name}
                                onChange={(val) => {
                                    setName(val);
                                    setFormErrors([]);
                                }}
                                onEnter={handleEnter}
                                autoFocus={true}
                                size='small'
                                onKeyDown={(e) => handleKeyDown(e, FIELD_NAME)}
                                textFieldRef={nameRef}
                                hasError={getFieldHasError()}
                            />
                            <TextField
                                placeholder='Description'
                                value={description}
                                onEnter={handleEnter}
                                onChange={(val) => setDescription(val)}
                                size='small'
                                onKeyDown={(e) => handleKeyDown(e, FIELD_DESCRIPTION)}
                                textFieldRef={descriptionRef}
                            />
                            <TextField
                                placeholder='Location'
                                size='small'
                                value={address}
                                onEnter={handleEnter}
                                onChange={(val) => {
                                    setAddress(val);
                                    setFormErrors([]);
                                }}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_ADDRESS)}
                                textFieldRef={addressRef}
                                hasError={getFieldHasError()}
                            />
                        </div>
                        <div className='flex flex-dir-col align-center gap-8'>
                            {props.temporaryAccount && (
                                <CheckmarkButton
                                    className='no-padding'
                                    size={12}
                                    onClick={handleSubmitAccount}
                                    variant='dark'
                                />
                            )}
                            <CloseButton className='no-padding' size={12} onClick={props.onClose} />
                        </div>
                    </div>
                    {formErrors && formErrors.length > 0 && (
                        <BulletedList
                            bullets={formErrors.map((error) => error)}
                            variant='errors'
                            size='small'
                            marginBottom={8}
                        />
                    )}
                </div>
            </BaseRow>
            {shouldAllowCreateAccount && (
                <div
                    styleName={
                        potentialAccounts.length > 0
                            ? 'create-button create-button-with-dropdown'
                            : 'create-button'
                    }
                >
                    <Button
                        variant='text-primary'
                        preventUnderline={true}
                        onClick={handleSubmitAccount}
                        isFullWidth={true}
                    >
                        + Create new company {name}
                    </Button>
                </div>
            )}
            {shouldShowPotentialAccountsComponent && potentialAccounts.length > 0 && (
                <PotentialPeepsDropdown
                    potentialPeeps={potentialAccounts}
                    onSelectPeep={props.onSelectAccount}
                    popoverRef={popoverRef}
                    canCreate={Boolean(shouldAllowCreate)}
                    isPopover={props.isPotentialAccountsPopover}
                />
            )}
        </>
    );
}
